import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { IS_PROD } from '@muzly/shared';

import { BootDismissOutlet } from './BootDismissOutlet';
import { RequireProfile } from './RequireProfile';
import { FEATURES } from '../core';
import { FeedbackPage } from '../features/feedback';
import { HomePage } from '../features/home';
import { LINK_ROUTE, LinkPage } from '../features/link';
import {
  ContactPage,
  FaqPage,
  LanguagePage,
  LicensesPage,
  MorePage,
} from '../features/more';
import {
  HistoryPage,
  OnboardingPage,
  ResultDetailPage,
  SetupPage,
  SharedResultPage,
} from '../features/onboarding';
import { PolicyPage } from '../features/policy';
import { ProfilePage } from '../features/profile';
import { TabLayout } from '../layouts';

/**
 * 디버그 콘솔은 **따로 받는 청크**다. 운영 빌드에는 라우트가 없으니 사용자 기기는 이 코드를
 * 영영 받지 않는다 — 정적으로 들이면 엔진 튜닝 패널·시계열 그래프가 홈과 같은 첫 청크에
 * 실려 WebView 첫 화면이 그만큼 무거워진다. 다른 화면은 정적으로 둔다: 페이지 전환이 새
 * 화면을 **동기 커밋**한 뒤 찍어야 하는데(`legacyFlushSync`), 지연 로드가 걸리면 전환이
 * Suspense 대기 화면을 찍는다.
 */
const DebugConsolePage = lazy(() =>
  import('../features/debug').then((m) => ({ default: m.DebugConsolePage })),
);

interface AppRoutesProps {
  /** 테스트 주입점. 기본은 빌드의 `IS_PROD`. */
  isProd?: boolean;
}

export const AppRoutes = ({ isProd = IS_PROD }: AppRoutesProps = {}) => {
  return (
    <Routes>
      {/* 게이트 밖 화면은 곧바로 그려지므로 첫 렌더에 부팅 대기 화면을 걷는다. 게이트 안은
          RequireProfile 이 복원을 마치고 열 때 걷는다. */}
      <Route element={<BootDismissOutlet />}>
        {/* 초기 설정 — 프로필이 없는 동안 앱의 유일한 화면. 게이트 밖에 있어야 게이트가
            여기로 보낼 수 있다. 탭바 없는 몰입 화면. */}
        <Route path="setup" element={<SetupPage />} />

        {/* 공유 링크 게이트웨이 — 앱이 있으면 앱, 없으면 웹. 게이트 밖이다: 프로필이 없어도 받아서
            목적지로 넘기고, 목적지가 게이트 안이면 그쪽이 초기 설정으로 보낸다. */}
        <Route path={LINK_ROUTE.slice(1)} element={<LinkPage />} />

        {/* 공유받은 결과(`/share/<analyId>`) — 링크를 연 사람은 프로필이 없다. 게이트 밖이다. */}
        <Route path="share/:id" element={<SharedResultPage />} />

        {/* 이용약관 공개 주소 — MY 의 약관 줄이 인앱 브라우저 모달로 연다(`VITE_TERMS_URL`).
            게이트 밖이다: 프로필 없이도, 앱 밖 브라우저에서도 읽혀야 한다. */}
        <Route
          path="policy/terms"
          element={<PolicyPage kind="terms" standalone />}
        />
        {/* 개인정보처리방침 공개 주소 — 같은 이유로 게이트 밖이다(`VITE_PRIVACY_URL`). */}
        <Route
          path="policy/privacy"
          element={<PolicyPage kind="privacy" standalone />}
        />

        {/* 엔진 관찰·튜닝·검증 디버그 콘솔 (web↔mobile↔native 풀체인). 게이트 밖 — 개발 도구.
            운영 빌드에는 등록하지 않는다 — 게이트 밖이라 딥링크·주소 입력 한 번이면 열린다. */}
        {!isProd && (
          <Route
            path="debug"
            element={
              <Suspense fallback={null}>
                <DebugConsolePage />
              </Suspense>
            }
          />
        )}
      </Route>

      {/* 프로필이 있어야 열리는 안쪽. */}
      <Route element={<RequireProfile />}>
        {/* 탭 셸 — 홈이 앱의 첫 화면이고, 진단은 홈 카드에서 들어간다. MY 아래에
            프로필 수정이 산다(시안 탭 `홈`/`MY`). */}
        <Route element={<TabLayout />}>
          <Route index element={<HomePage />} />
          <Route path="more" element={<MorePage />} />
          <Route path="more/profile" element={<ProfilePage />} />
          <Route path="more/language" element={<LanguagePage />} />
          {/* MY 의 문서(약관·방침·문의·라이선스·자주 묻는 질문). 드릴다운이라 탭바를 감춘다.
              문서마다 자기 화면이다: 라이선스는 본문이 데이터, 자주 묻는 질문은 아코디언, 약관·
              방침은 시행일별 버전(`features/policy`), 문의는 메일 버튼. 모르는 문서 주소는 맨 아래
              catch-all 이 받는다. */}
          <Route path="more/docs/licenses" element={<LicensesPage />} />
          <Route path="more/docs/faq" element={<FaqPage />} />
          <Route path="more/docs/terms" element={<PolicyPage kind="terms" />} />
          <Route
            path="more/docs/privacy"
            element={<PolicyPage kind="privacy" />}
          />
          <Route path="more/docs/contact" element={<ContactPage />} />
          {/* API 가 없는 동안은 라우트도 없다 — 줄만 가리면 주소로 들어온다(catch-all 이 홈으로). */}
          {FEATURES.feedback && (
            <Route path="more/feedback" element={<FeedbackPage />} />
          )}

          {/* 아래 셋은 탭바를 감추는 몰입 화면이지만 라우트는 셸 **안**이다.
              밖에 두면 진단으로 들어갈 때 탭 서브트리가 통째로 unmount되고, 그
              상태로 View Transitions 스냅샷이 잡혀 WKWebView에서 깜빡인다
              (`epyt-page-transition-reconciler-fix` F2). 감추는 쪽은 TabLayout이
              `fullscreenPaths`로 판정한다. */}

          {/* 진단 회차(회차 질문 + 녹음 + 결과). */}
          <Route path="diagnosis" element={<OnboardingPage />} />

          {/* 진단 회차 이력. */}
          <Route path="history" element={<HistoryPage />} />

          {/* 회차 하나의 지표 상세(시안 §8.2). 탭 이름이 주소에 있어야 공유한 링크가
              어느 지표를 말하는지까지 담는다. 탭 없이 열면 첫 탭으로 선다. */}
          <Route path="result/:id" element={<ResultDetailPage />} />
          <Route path="result/:id/:tab" element={<ResultDetailPage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
  );
};
